// Team Timesheet — reporting-manager / admin review of subordinate day-end
// reports. Route: /employee/team/timesheet  (sidebar: "Team Timesheet")
// Gated by the `team_timesheet` permission key; the backend enforces team
// scope + permission regardless of what the UI shows. Mirrors the mockup's
// review center: approve / reject / request correction + hours & productivity
// override, all written to an immutable audit trail.

import { useState } from "react";
import { Users, ShieldAlert, Eye, History, CheckCircle2, XCircle, RotateCcw, Save } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard, EmployeeDetails } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { TEAM_TIMESHEET_URL } from "../../constants/api";

const PRODUCTIVITY_OPTIONS = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const STATUS_CONFIG = {
    draft: { text: "Draft", className: "bg-gray-100 text-gray-600 border-gray-200" },
    pending: { text: "Pending Review", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    approved: { text: "Approved", className: "bg-green-100 text-green-700 border-green-200" },
    rejected: { text: "Rejected", className: "bg-red-100 text-red-700 border-red-200" },
    correction: { text: "Correction Requested", className: "bg-blue-100 text-blue-700 border-blue-200" },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || {
        text: status ? status.charAt(0).toUpperCase() + status.slice(1) : "-",
        className: "bg-blue-100 text-blue-700 border-blue-200",
    };
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${cfg.className}`}>
            {cfg.text}
        </span>
    );
};

// Map the backend employee_profile_details() shape onto EmployeeDetails props.
const toEmployeeDetails = (employee) => ({
    ...employee,
    first_name: employee?.employee_name || "-",
    last_name: "",
    id: employee?.employee_id || "",
    designation_id: employee?.designation
        ? { id: employee.designation.id, designation_name: employee.designation.name }
        : { id: 0, designation_name: "" },
});

const EmployeeTeamTimesheet = () => {
    const { update } = useApiMethods();
    const permission = checkUserPermissionFunc("employee", "Team Timesheet", "SubMenu") || {};
    const canReview = !!(permission.permission || permission.edit);

    // table
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    // review dialog
    const [openReview, setOpenReview] = useState(false);
    const [selected, setSelected] = useState(null);
    const [reviewState, setReviewState] = useState({ comment: "", adj_hours: "", adj_productivity: "High" });
    const [loading, setLoading] = useState(false);

    // detail dialog
    const [openDetail, setOpenDetail] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);

    const openReviewModal = (row) => {
        setSelected(row);
        setReviewState({
            comment: row.manager_comment || "",
            adj_hours: row.actual_hours ?? "",
            adj_productivity: row.productivity || "High",
        });
        setOpenReview(true);
    };

    const doReview = async (action) => {
        if (!selected) return;
        if ((action === "rejected" || action === "correction") && !reviewState.comment.trim()) {
            ToastMsg("error", "A comment is required for this action");
            return;
        }
        const payload = {
            id: selected.id,
            action, // "" → save edits only
            comment: reviewState.comment,
            adj_hours: reviewState.adj_hours === "" ? undefined : Number(reviewState.adj_hours),
            adj_productivity: reviewState.adj_productivity,
        };

        setLoading(true);
        try {
            const res = await update(TEAM_TIMESHEET_URL, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Review saved");
                setOpenReview(false);
                setSelected(null);
                setServerTrigger((p) => !p);
            } else {
                ToastMsg("error", res?.message || "Failed to submit review");
            }
        } catch (e) {
            ToastMsg("error", "Something went wrong while reviewing");
        } finally {
            setLoading(false);
        }
    };

    // Days a rejected report stays open for a correction request.
    const CORRECTION_WINDOW_DAYS = 2;

    // Which review actions are available for the selected report:
    //  - pending / correction → full set (approve, reject, request correction, save edits)
    //  - approved             → none (terminal)
    //  - rejected             → only "Request Correction", and only within
    //                           CORRECTION_WINDOW_DAYS of the request (created) date
    const reviewStatus = selected?.status;
    const isApproved = reviewStatus === "approved";
    const isRejected = reviewStatus === "rejected";
    const editable = !!selected && !isApproved && !isRejected;
    const requestDate = selected?.created_date
        ? dayjs(selected.created_date)
        : (selected?.date ? dayjs(selected.date) : null);
    const withinCorrectionWindow =
        !!requestDate && dayjs().isBefore(requestDate.add(CORRECTION_WINDOW_DAYS, "day").endOf("day"));
    const showCorrection = editable || (isRejected && withinCorrectionWindow);
    const showAnyAction = editable || showCorrection;
    const noActionNote = isApproved
        ? "This report has been approved — no further action."
        : isRejected
            ? "Rejected · the correction window has closed."
            : null;

    const columns = [
        {
            Header: "Employee",
            accessor: "employee",
            filterable: true,
            width: "220px",
            Cell: ({ value }) => <EmployeeDetails details={toEmployeeDetails(value)} size="xs" isFromInbox />,
        },
        {
            Header: "Department",
            accessor: "department",
            sortable: true,
            filterable: true,
            width: "140px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value || "-"}</span>,
        },
        {
            Header: "Date",
            accessor: "date",
            sortable: true,
            filterable: true,
            filterType: "range",
            width: "120px",
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {value ? dayjs(value).format("DD MMM YYYY") : "-"}
                </span>
            ),
        },
        {
            Header: "Project",
            accessor: "project",
            sortable: true,
            filterable: true,
            width: "180px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value || "-"}</span>,
        },
        {
            Header: "Hours",
            accessor: "actual_hours",
            sortable: true,
            width: "80px",
            Cell: ({ value }) => <span className="font-medium text-gray-700 dark:text-gray-300">{value ?? 0}h</span>,
        },
        {
            Header: "Tasks",
            accessor: "tasks",
            width: "70px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value?.length || 0}</span>,
        },
        {
            Header: "Status",
            accessor: "status",
            sortable: true,
            filterable: true,
            width: "150px",
            Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
            Header: "Actions",
            accessor: "actions",
            sortable: false,
            fixed: true,
            align: "right",
            width: "120px",
            Cell: ({ row }) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => {
                            setDetailRecord(row);
                            setOpenDetail(true);
                        }}
                        className="h-7 w-7 p-0 rounded-md bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100"
                        title="View Report"
                    >
                        {row.status === "pending" ? <Eye className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
                    </Button>
                    {canReview && (row.status === "pending" || row.status === "rejected" || withinCorrectionWindow ) && (
                        <Button
                            size="sm"
                            onClick={() => openReviewModal(row)}
                            className="h-7 bg-sidebar hover:bg-sidebar-primary text-white"
                            title="Review"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    if (!canReview) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center h-[60vh] text-center gap-3">
                    <ShieldAlert className="h-12 w-12 text-amber-500" />
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Access Restricted</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        You don't have permission to view Team Timesheets. Please contact your administrator
                        if you believe this is a mistake.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Users}
                title="Team Timesheet"
                subtitle="Review and approve day-end reports from your team"
                totalRecords={totalRecords}
            />

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                serverSide
                url={TEAM_TIMESHEET_URL}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize="md"
                isCSVRequired
                isPDFRequired
            />

            {/* Review dialog (custom actions → showActions=false) */}
            <DialogModal
                open={openReview}
                onClose={() => {
                    setOpenReview(false);
                    setSelected(null);
                }}
                title="Review Day-End Report"
                showActions={false}
                width="82rem"
            >
                {selected && (
                    <div className="space-y-4 flex flex-col">
                        {/* Two parallel containers: report + manager actions (left),
                            approval log (right). */}
                        <div className="grid col-span-2 grid-cols-[70%_30%] overflow-auto max-h-[65vh]">
                            {/* Left — report details + manager actions */}
                            <div className="space-y-4 overflow-y-auto pr-1">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <EmployeeDetails details={toEmployeeDetails(selected.employee)} size="sm" isFromInbox />
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {selected.date ? dayjs(selected.date).format("DD MMM YYYY") : "-"}
                                        </span>
                                        <StatusBadge status={selected.status} />
                                    </div>
                                </div>

                                {/* tasks */}
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hrs</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {(selected.tasks || []).map((t, i) => (
                                                <tr key={i}>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{t.description}</td>
                                                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{t.module || "-"}</td>
                                                    <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{t.time_spent ?? 0}h</td>
                                                </tr>
                                            ))}
                                            {!selected.tasks?.length && (
                                                <tr>
                                                    <td colSpan={3} className="px-3 py-4 text-center text-gray-400">No tasks</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <DetailRow label="Achievements" value={selected.achievements} />
                                <DetailRow label="Blockers" value={selected.blockers} />
                                <DetailRow label="Next Day Plan" value={selected.next_day_plan} />

                                {/* manager adjustments — hidden once no action is possible */}
                                {showAnyAction && (
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/60 dark:bg-gray-800/40 space-y-3">
                                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">Manager Actions</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="space-y-2">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Logged Hours</label>
                                            <Input
                                                type="number"
                                                min={0}
                                                step="0.5"
                                                value={reviewState.adj_hours}
                                                onChange={(e) => setReviewState((p) => ({ ...p, adj_hours: e.target.value }))}
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Productivity</label>
                                            <Select
                                                value={reviewState.adj_productivity}
                                                onChange={(v) => setReviewState((p) => ({ ...p, adj_productivity: v }))}
                                                className="w-full"
                                                size="large"
                                                options={PRODUCTIVITY_OPTIONS}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            Review Comment / Reason for Change
                                        </label>
                                        <Textarea
                                            value={reviewState.comment}
                                            onChange={(e) => setReviewState((p) => ({ ...p, comment: e.target.value }))}
                                            placeholder="Required for reject / request correction..."
                                            rows={2}
                                            maxLength={1000}
                                            showCharCount
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                )}
                            </div>

                            {/* Right — approval log */}
                            <ApprovalLogPanel logs={selected.approval_logs} />
                        </div>

                        <div className="flex flex-wrap items-center gap-2 justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
                            {showAnyAction ? (
                                <>
                                    {editable && (
                                        <Button
                                            variant="outline"
                                            disabled={loading}
                                            onClick={() => doReview("rejected")}
                                            className="text-red-600 border-red-200 hover:bg-red-50"
                                        >
                                            <XCircle className="h-4 w-4 mr-1" /> Reject
                                        </Button>
                                    )}
                                    {showCorrection && (
                                        <Button
                                            variant="outline"
                                            disabled={loading}
                                            onClick={() => doReview("correction")}
                                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                        >
                                            <RotateCcw className="h-4 w-4 mr-1" /> Request Correction
                                        </Button>
                                    )}
                                    {editable && (
                                        <Button
                                            variant="outline"
                                            disabled={loading}
                                            onClick={() => doReview("")}
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            <Save className="h-4 w-4 mr-1" /> Save Edits
                                        </Button>
                                    )}
                                    {editable && (
                                        <Button
                                            disabled={loading}
                                            onClick={() => doReview("approved")}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                        >
                                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <span className="text-sm text-gray-500 dark:text-gray-400">{noActionNote}</span>
                            )}
                        </div>
                    </div>
                )}
            </DialogModal>

            {/* Detail dialog (read-only, with audit trail) */}
            <DialogModal
                open={openDetail}
                onClose={() => setOpenDetail(false)}
                title="Report Detail"
                showActions={false}
                width="83rem"
            >
                {detailRecord && (
                    <div className="grid col-span-2 grid-cols-[70%_30%] gap-3 max-h-[75vh]">
                        {/* Left — report details */}
                        <div className="space-y-4 overflow-y-auto mb-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <EmployeeDetails details={toEmployeeDetails(detailRecord.employee)} size="sm" isFromInbox />
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        {detailRecord.actual_hours ?? 0}h • {detailRecord.productivity || "-"}
                                    </span>
                                    <StatusBadge status={detailRecord.status} />
                                </div>
                            </div>

                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hrs</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {(detailRecord.tasks || []).map((t, i) => (
                                            <tr key={i}>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{t.description}</td>
                                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{t.module || "-"}</td>
                                                <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{t.time_spent ?? 0}h</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <DetailRow label="Achievements" value={detailRecord.achievements} />
                            <DetailRow label="Blockers" value={detailRecord.blockers} />
                            <DetailRow label="Next Day Plan" value={detailRecord.next_day_plan} />
                            {detailRecord.manager_comment && (
                                <div className="rounded-lg border border-sidebar/30 bg-sidebar/5 px-3 py-2 text-sm">
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">Manager Comment: </span>
                                    <span className="text-gray-600 dark:text-gray-400">{detailRecord.manager_comment}</span>
                                </div>
                            )}
                        </div>

                        {/* Right — approval log */}
                        <ApprovalLogPanel logs={detailRecord.approval_logs} />
                    </div>
                )}
            </DialogModal>
        </ContainerCard>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm">
        <span className="font-semibold text-gray-700 dark:text-gray-300">{label}: </span>
        <span className="text-gray-600 dark:text-gray-400">{value || "—"}</span>
    </div>
);

// Right-hand approval-trail container, shown alongside the report details.
const ApprovalLogPanel = ({ logs }) => (
    <div className="flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/30 p-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <History className="h-4 w-4" /> Approval History
        </div>
        <div className="space-y-2 overflow-y-auto pr-1 flex-1">
            {logs?.length > 0 ? (
                logs.map((log, i) => (
                    <div
                        key={i}
                        className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{log.action}</span>
                            <span className="text-gray-400">
                                {log.performed_at ? new Date(log.performed_at).toLocaleString() : ""}
                            </span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 mt-1">
                            {log.old_value || log.new_value ? (
                                <span>
                                    <span className="line-through text-red-500">{log.old_value || "—"}</span>
                                    {" → "}
                                    <span className="text-green-600 font-medium">{log.new_value || "—"}</span>
                                </span>
                            ) : null}
                            {log.reason ? <span className="italic"> • {log.reason}</span> : null}
                            {log.performed_by ? <span> • {log.performed_by}</span> : null}
                        </div>
                    </div>
                ))
            ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                    No approval history yet
                </div>
            )}
        </div>
    </div>
);

export default EmployeeTeamTimesheet;
